import { Controller } from "stimulus"

/**
 * ## DirectUploadController
 *
 * # Provides progress feedback and file-size warnings for Active Storage
 * # direct uploads. Attach to a container (e.g. <body>) so it catches
 * # bubbled events from every direct-upload file input on the page.
 */
export default class extends Controller {
  static targets = []

  // 500 MB warning threshold (in bytes)
  static FILE_SIZE_WARNING = 500 * 1024 * 1024

  connect() {
    this._inFlightCounts = new WeakMap()

    this._onInitialize = this._handleInitialize.bind(this)
    this._onStart = this._handleStart.bind(this)
    this._onProgress = this._handleProgress.bind(this)
    this._onError = this._handleError.bind(this)
    this._onEnd = this._handleEnd.bind(this)
    this._onUploadsEnd = this._handleUploadsEnd.bind(this)

    this.element.addEventListener("direct-upload:initialize", this._onInitialize)
    this.element.addEventListener("direct-upload:start", this._onStart)
    this.element.addEventListener("direct-upload:progress", this._onProgress)
    this.element.addEventListener("direct-upload:error", this._onError)
    this.element.addEventListener("direct-upload:end", this._onEnd)
    this.element.addEventListener("direct-uploads:end", this._onUploadsEnd)
  }

  disconnect() {
    this.element.removeEventListener("direct-upload:initialize", this._onInitialize)
    this.element.removeEventListener("direct-upload:start", this._onStart)
    this.element.removeEventListener("direct-upload:progress", this._onProgress)
    this.element.removeEventListener("direct-upload:error", this._onError)
    this.element.removeEventListener("direct-upload:end", this._onEnd)
    this.element.removeEventListener("direct-uploads:end", this._onUploadsEnd)
  }

  _handleInitialize(event) {
    const { target, detail } = event
    const { id, file } = detail

    if (file.size > this.constructor.FILE_SIZE_WARNING) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(0)
      const message = `"${file.name}" is ${sizeMB} MB. Large uploads may take several minutes. Continue?`
      if (!window.confirm(message)) {
        event.preventDefault()
        return
      }
    }

    const form = target.closest("form")
    if (form) {
      this._incrementInFlight(form)
      if (this._getInFlight(form) === 1) {
        this._disableSubmitButtons(form)
      }
    }

    const container = this._getOrCreateProgressContainer(target)
    const progressEl = this._createProgressElement(id, file.name)
    container.appendChild(progressEl)
  }

  _handleStart(event) {
    const { id } = event.detail
    const progressEl = document.getElementById(`direct-upload-${id}`)
    if (progressEl) {
      progressEl.classList.remove("bg-gray-100")
      progressEl.classList.add("bg-blue-50")
    }
  }

  _handleProgress(event) {
    const { id, progress } = event.detail
    const bar = document.getElementById(`direct-upload-progress-${id}`)
    if (bar) {
      bar.style.width = `${progress}%`
    }
    const label = document.getElementById(`direct-upload-label-${id}`)
    if (label) {
      label.textContent = `${Math.round(progress)}%`
    }
  }

  _handleError(event) {
    const { id, error } = event.detail
    event.preventDefault()

    const progressEl = document.getElementById(`direct-upload-${id}`)
    if (progressEl) {
      progressEl.classList.remove("bg-blue-50")
      progressEl.classList.add("bg-red-50")
      const label = document.getElementById(`direct-upload-label-${id}`)
      if (label) {
        label.textContent = "Failed"
        label.classList.add("text-red-600")
      }
      const bar = document.getElementById(`direct-upload-progress-${id}`)
      if (bar) {
        bar.classList.remove("bg-purple-500")
        bar.classList.add("bg-red-500")
      }
    }

    const form = event.target.closest("form")
    if (form) {
      this._decrementInFlight(form)
      if (this._getInFlight(form) === 0) {
        this._enableSubmitButtons(form)
      }
    }

    const errorContainer = document.createElement("p")
    errorContainer.className = "text-sm text-red-600 mt-1"
    errorContainer.textContent = `Upload failed: ${error}`
    if (progressEl) {
      progressEl.parentNode.insertBefore(errorContainer, progressEl.nextSibling)
    }
  }

  _handleEnd(event) {
    const { id } = event.detail
    const progressEl = document.getElementById(`direct-upload-${id}`)
    if (progressEl) {
      progressEl.classList.remove("bg-blue-50")
      progressEl.classList.add("bg-green-50")
      const label = document.getElementById(`direct-upload-label-${id}`)
      if (label) {
        label.textContent = "Done"
        label.classList.add("text-green-600")
      }
    }

    const form = event.target.closest("form")
    if (form) {
      this._decrementInFlight(form)
    }
  }

  _handleUploadsEnd(event) {
    const form = event.target.closest("form")
    if (form) {
      this._resetInFlight(form)
      this._enableSubmitButtons(form)
    }
  }

  _getOrCreateProgressContainer(input) {
    let container = input.parentElement.querySelector("[data-direct-upload-progress-container]")
    if (!container) {
      container = document.createElement("div")
      container.setAttribute("data-direct-upload-progress-container", "true")
      container.className = "mt-2 space-y-1"
      input.parentElement.appendChild(container)
    }
    return container
  }

  _createProgressElement(id, filename) {
    const el = document.createElement("div")
    el.id = `direct-upload-${id}`
    el.className = "flex items-center gap-2 rounded-lg bg-gray-100 p-2 text-sm"

    const nameSpan = document.createElement("span")
    nameSpan.className = "truncate max-w-[200px]"
    nameSpan.textContent = filename
    nameSpan.title = filename

    const barContainer = document.createElement("div")
    barContainer.className = "flex-1 h-2 bg-gray-200 rounded-full overflow-hidden"

    const bar = document.createElement("div")
    bar.id = `direct-upload-progress-${id}`
    bar.className = "h-full bg-purple-500 rounded-full transition-all duration-150"
    bar.style.width = "0%"
    barContainer.appendChild(bar)

    const label = document.createElement("span")
    label.id = `direct-upload-label-${id}`
    label.className = "text-xs text-gray-500 w-12 text-right"
    label.textContent = "0%"

    el.appendChild(nameSpan)
    el.appendChild(barContainer)
    el.appendChild(label)

    return el
  }

  _disableSubmitButtons(form) {
    form.querySelectorAll("input[type=submit], button[type=submit]").forEach(btn => {
      if (!btn.dataset.directUploadOriginalText) {
        btn.dataset.directUploadOriginalText = btn.value || btn.textContent
      }
      btn.disabled = true
      if (btn.tagName === "INPUT") {
        btn.value = "Uploading\u2026"
      } else {
        btn.textContent = "Uploading\u2026"
      }
    })
  }

  _enableSubmitButtons(form) {
    form.querySelectorAll("input[type=submit], button[type=submit]").forEach(btn => {
      btn.disabled = false
      const original = btn.dataset.directUploadOriginalText
      if (original) {
        if (btn.tagName === "INPUT") {
          btn.value = original
        } else {
          btn.textContent = original
        }
        delete btn.dataset.directUploadOriginalText
      }
    })
  }

  _getInFlight(form) {
    return this._inFlightCounts.get(form) || 0
  }

  _incrementInFlight(form) {
    this._inFlightCounts.set(form, this._getInFlight(form) + 1)
  }

  _decrementInFlight(form) {
    const count = this._getInFlight(form)
    this._inFlightCounts.set(form, Math.max(0, count - 1))
  }

  _resetInFlight(form) {
    this._inFlightCounts.set(form, 0)
  }
}
